'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdSearch,
    MdClose,
    MdHistory,
    MdRefresh,
    MdFilterList,
    MdVisibility,
} from 'react-icons/md'
import {
    getTissTransactions,
    TissTransaction,
    TissStatus,
    TissTransactionType,
    TISS_STATUS_LABELS,
    TISS_STATUS_COLORS,
    TISS_TRANSACTION_TYPE_LABELS,
    TISS_GUIDE_TYPE_LABELS,
} from '@/lib/api/health-integrations/tiss-api'

export default function TissTransactionsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()

    const [transactions, setTransactions] = useState<TissTransaction[]>([])
    const [filteredTransactions, setFilteredTransactions] = useState<
        TissTransaction[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<TissStatus | ''>('')
    const [typeFilter, setTypeFilter] = useState<TissTransactionType | ''>('')
    const [selectedTransaction, setSelectedTransaction] =
        useState<TissTransaction | null>(null)

    useEffect(() => {
        if (user?.companyId) {
            loadTransactions()
        }
    }, [user?.companyId, statusFilter, typeFilter])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = transactions.filter(
                transaction =>
                    transaction.guideNumber
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    transaction.client?.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    transaction.credential?.healthPlan?.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredTransactions(filtered)
        } else {
            setFilteredTransactions(transactions)
        }
    }, [searchTerm, transactions])

    const loadTransactions = async () => {
        setLoading(true)
        try {
            const filters: any = {}
            if (statusFilter) filters.status = statusFilter
            if (typeFilter) filters.type = typeFilter

            const response = await getTissTransactions(filters)
            if (response.success) {
                setTransactions(response.result)
                setFilteredTransactions(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar transações')
            }
        } catch (error) {
            toast.error('Erro ao carregar transações')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando transações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/health-integrations/tiss')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                            <MdHistory className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                Transações TISS
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {transactions.length}{' '}
                                {transactions.length === 1
                                    ? 'transação'
                                    : 'transações'}
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    onClick={loadTransactions}
                    className="btn btn-ghost btn-sm gap-2"
                >
                    <MdRefresh className="text-xl" />
                    Atualizar
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por guia, beneficiário ou operadora..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <MdFilterList className="text-xl text-base-content/60" />
                        <select
                            className="select select-bordered select-sm"
                            value={statusFilter}
                            onChange={e =>
                                setStatusFilter(
                                    e.target.value as TissStatus | ''
                                )
                            }
                        >
                            <option value="">Status</option>
                            {Object.entries(TISS_STATUS_LABELS).map(
                                ([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                )
                            )}
                        </select>
                        <select
                            className="select select-bordered select-sm"
                            value={typeFilter}
                            onChange={e =>
                                setTypeFilter(
                                    e.target.value as TissTransactionType | ''
                                )
                            }
                        >
                            <option value="">Tipo</option>
                            {Object.entries(TISS_TRANSACTION_TYPE_LABELS).map(
                                ([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredTransactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdHistory className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma transação encontrada
                        </h3>
                        <p className="text-sm text-base-content/60">
                            {searchTerm || statusFilter || typeFilter
                                ? 'Tente ajustar seus filtros'
                                : 'Transações TISS aparecerão aqui'}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Convênio</th>
                                    <th>Beneficiario</th>
                                    <th>Guia</th>
                                    <th>Status</th>
                                    <th>Data</th>
                                    <th>Acoes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTransactions.map(transaction => (
                                    <tr key={transaction.id} className="hover">
                                        <td>
                                            <div>
                                                <span className="badge badge-outline badge-sm">
                                                    {
                                                        TISS_TRANSACTION_TYPE_LABELS[
                                                            transaction.type
                                                        ]
                                                    }
                                                </span>
                                                {transaction.guideType && (
                                                    <span className="text-xs text-base-content/60 block mt-1">
                                                        {
                                                            TISS_GUIDE_TYPE_LABELS[
                                                                transaction
                                                                    .guideType
                                                            ]
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {transaction.credential?.healthPlan
                                                ?.name || '-'}
                                        </td>
                                        <td>
                                            {transaction.client?.name || '-'}
                                            {transaction.client?.cpf && (
                                                <span className="text-xs text-base-content/60 block">
                                                    {transaction.client.cpf}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {transaction.guideNumber || '-'}
                                        </td>
                                        <td>
                                            <span
                                                className={`badge ${TISS_STATUS_COLORS[transaction.status]}`}
                                            >
                                                {
                                                    TISS_STATUS_LABELS[
                                                        transaction.status
                                                    ]
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(
                                                transaction.createdAt
                                            ).toLocaleString('pt-BR')}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() =>
                                                    setSelectedTransaction(
                                                        transaction
                                                    )
                                                }
                                                className="btn btn-ghost btn-sm"
                                                title="Ver detalhes"
                                            >
                                                <MdVisibility className="text-lg" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Transaction Detail Modal */}
            {selectedTransaction && (
                <dialog open className="modal modal-open">
                    <div className="modal-box max-w-4xl">
                        <h3 className="font-bold text-lg mb-4">
                            Detalhes da Transação
                        </h3>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Tipo
                                    </label>
                                    <p className="font-medium">
                                        {
                                            TISS_TRANSACTION_TYPE_LABELS[
                                                selectedTransaction.type
                                            ]
                                        }
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Status
                                    </label>
                                    <p>
                                        <span
                                            className={`badge ${TISS_STATUS_COLORS[selectedTransaction.status]}`}
                                        >
                                            {
                                                TISS_STATUS_LABELS[
                                                    selectedTransaction.status
                                                ]
                                            }
                                        </span>
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Convênio
                                    </label>
                                    <p className="font-medium">
                                        {selectedTransaction.credential
                                            ?.healthPlan?.name || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Beneficiário
                                    </label>
                                    <p className="font-medium">
                                        {selectedTransaction.client?.name || '-'}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        Número da Guia
                                    </label>
                                    <p className="font-medium">
                                        {selectedTransaction.guideNumber || '-'}
                                    </p>
                                </div>
                                {selectedTransaction.authorizationCode && (
                                    <div>
                                        <label className="text-sm text-base-content/60">
                                            Senha de autorização
                                        </label>
                                        <p className="font-medium">
                                            {
                                                selectedTransaction.authorizationCode
                                            }
                                        </p>
                                    </div>
                                )}
                                {selectedTransaction.protocolNumber && (
                                    <div>
                                        <label className="text-sm text-base-content/60">
                                            Protocolo ANS
                                        </label>
                                        <p className="font-medium">
                                            {selectedTransaction.protocolNumber}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {selectedTransaction.denialReason && (
                                <div className="alert alert-error">
                                    <span>
                                        {selectedTransaction.denialReason}
                                    </span>
                                </div>
                            )}

                            {selectedTransaction.requestXml && (
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        XML de Requisição
                                    </label>
                                    <pre className="bg-base-200 p-4 rounded-lg text-xs overflow-x-auto max-h-40">
                                        {selectedTransaction.requestXml}
                                    </pre>
                                </div>
                            )}

                            {selectedTransaction.responseXml && (
                                <div>
                                    <label className="text-sm text-base-content/60">
                                        XML de Resposta
                                    </label>
                                    <pre className="bg-base-200 p-4 rounded-lg text-xs overflow-x-auto max-h-40">
                                        {selectedTransaction.responseXml}
                                    </pre>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => setSelectedTransaction(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setSelectedTransaction(null)}>
                            close
                        </button>
                    </form>
                </dialog>
            )}
        </div>
    )
}
